import { Button, Divider, Flex, Input, Row, Tabs, Typography } from 'antd';
import { useDebouncedState } from '../../hooks/useDebouncedState';
import { CatalogItem } from '../../domain/catalogItem';
import { CatalogTemplateWithItems } from '../../domain/catalogTemplate';
import ItemList from './ItemList';
import TemplatesList from './TemplatesList';
import { useSavedState } from '../../hooks/useSavedState';
import ItemFilters from './ItemFilters';
import { useRef, useState } from 'react';
import AddItemForm from './AddItemForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faXmark } from '@fortawesome/pro-solid-svg-icons';
import IconAndTextButton from '../IconAndTextButton';
import VSpace from '../common/VSpace';

const { Text } = Typography
export type Props = {
  draggable?: boolean
  onAdd: (item: CatalogItem) => void
  onTemplateAdd?: (item: CatalogTemplateWithItems) => void
  fixedHeight?: string
  docked?: boolean
  onClose?: () => void
  width?: string
  hideTitle?: boolean
  horizontalPadding?: number
  anchorNewItemModal?: boolean
}

export const ItemSelector = ({ onAdd = () => { }, draggable = false, onTemplateAdd, fixedHeight, docked, onClose, width, hideTitle, anchorNewItemModal, horizontalPadding = 6 }: Props) => {
  const {
    state: searchQuery,
    debouncedState: debouncedSearchQuery,
    setState: setSearchQuery
  } = useDebouncedState('', 250)

  const [selectedClassIds, setSelectedClassIds] = useSavedState<number[]>('item-selector:class-ids', [])
  const [selectedCategories, setSelectedCategories] = useSavedState<string[]>('item-selector:category-ids', [])
  const [filtersExpanded, setFiltersExpanded] = useSavedState<boolean>('item-select:filters-expanded', false)
  const [addFormVisible, setAddFormVisible] = useState(false)
  const containerRef = useRef<any>(null)

  const itemList = <ItemList search={debouncedSearchQuery}
    draggable={draggable}
    selectedClassIds={selectedClassIds}
    selectedCategories={selectedCategories}
    onAdd={onAdd}
    filterOpen={filtersExpanded}
    fixedHeight={fixedHeight}
    docked={docked} />

  const tabs = onTemplateAdd ? <Tabs
    defaultActiveKey="1"
    type="card"
    size="small"
    items={[
      { key: 'catalog', label: 'Items', children: itemList },
      {
        key: 'templates', label: 'Templates', children: <TemplatesList
          search={debouncedSearchQuery}
          selectedCategories={selectedCategories}
          onAddTemplate={onTemplateAdd}
          draggable={draggable}
          filterOpen={filtersExpanded}
          fixedHeight={fixedHeight}
          docked={docked}
        />
      }
    ]}
  /> : <></>

  const onAddNewItem = (catalogItem: CatalogItem) => {
    onAdd(catalogItem)
    setAddFormVisible(false)
  }

  const widthValue = width ? width : docked ? 280 : 400;

  return <div ref={containerRef}>
    <Flex vertical gap={16} style={{ width: widthValue, backgroundColor: '#FAFAFA', paddingTop: 16, paddingLeft: horizontalPadding, paddingRight: horizontalPadding }}>
      {hideTitle !== true && <Row justify="space-between" align="middle">
        <Text style={{ fontSize: 18, fontFamily: 'Open Sans, sans-serif' }}>Catalog Browser</Text>
        {onClose && <Button ghost icon={<FontAwesomeIcon icon={faXmark} />} onClick={onClose} style={{ color: 'black' }} />}
      </Row>}
      <Row>
        <Input.Search placeholder="Search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
      </Row>

      <AddItemForm
        visible={addFormVisible}
        getContainer={anchorNewItemModal ? () => containerRef.current : undefined}
        onCreated={onAddNewItem}
        onCancel={() => setAddFormVisible(false)}
      />
      <ItemFilters
        selectedClassIds={selectedClassIds}
        onSelectedClassIdsChange={setSelectedClassIds}
        selectedCategories={selectedCategories}
        onSelectedCategoriesChange={setSelectedCategories}
        expanded={filtersExpanded}
        onExpandedChange={expanded => setFiltersExpanded(expanded)}
      />
      <VSpace>
        <div>
          <IconAndTextButton onClick={() => setAddFormVisible(true)} icon={faPlus} text="New Item" style={{ width: "100%" }} />
          <Divider />
        </div>
        {onTemplateAdd ? tabs : itemList}
      </VSpace>
    </Flex>
  </div>
}

export default ItemSelector